/**
 * HubSpot Integration Factory
 * Creates and validates HubSpot integration instances
 */

import { IntegrationFactory } from '../registry/IntegrationRegistry';
import { HubSpotIntegration, HubSpotConfig } from './HubSpotIntegration';
import { IntegrationConfig, ValidationResult } from '../../../types/integration';

export class HubSpotIntegrationFactory implements IntegrationFactory {
  
  async create(config: IntegrationConfig): Promise<HubSpotIntegration> {
    const hubspotConfig = config as HubSpotConfig;
    
    // Validate configuration before creating
    const validation = await this.validate(hubspotConfig);
    if (!validation.valid) {
      throw new Error(`Invalid HubSpot configuration: ${validation.errors.map(e => e.message).join(', ')}`);
    }
    
    return new HubSpotIntegration(hubspotConfig);
  }

  async validate(config: Partial<IntegrationConfig>): Promise<ValidationResult> {
    const hubspotConfig = config as Partial<HubSpotConfig>;
    const errors: Array<{ field: string; message: string; code: string }> = [];
    const warnings: Array<{ field: string; message: string; code: string }> = [];

    // Basic validation
    if (!hubspotConfig.name) {
      errors.push({
        field: 'name',
        message: 'Integration name is required',
        code: 'REQUIRED'
      });
    }

    // Authentication validation
    if (hubspotConfig.useOAuth) {
      if (!hubspotConfig.clientId) {
        errors.push({
          field: 'clientId',
          message: 'Client ID is required for OAuth authentication',
          code: 'REQUIRED'
        });
      }
      
      if (!hubspotConfig.clientSecret) {
        errors.push({
          field: 'clientSecret',
          message: 'Client Secret is required for OAuth authentication',
          code: 'REQUIRED'
        });
      }
      
      if (!hubspotConfig.redirectUri) {
        errors.push({
          field: 'redirectUri',
          message: 'Redirect URI is required for OAuth authentication',
          code: 'REQUIRED'
        });
      }
      
      if (!hubspotConfig.scopes || hubspotConfig.scopes.length === 0) {
        warnings.push({
          field: 'scopes',
          message: 'No OAuth scopes specified, using default scopes',
          code: 'DEFAULT_VALUE'
        });
      }
    } else {
      if (!hubspotConfig.apiKey) {
        errors.push({
          field: 'apiKey',
          message: 'API Key is required for API key authentication',
          code: 'REQUIRED'
        });
      }
    }

    // Validate scopes if provided
    if (hubspotConfig.scopes) {
      const validScopes = this.getValidScopes();
      const invalidScopes = hubspotConfig.scopes.filter(scope => !validScopes.includes(scope));
      
      if (invalidScopes.length > 0) {
        warnings.push({
          field: 'scopes',
          message: `Invalid scopes detected: ${invalidScopes.join(', ')}`,
          code: 'INVALID_SCOPE'
        });
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  getConfigSchema(): Record<string, any> {
    return {
      type: 'object',
      properties: {
        name: {
          type: 'string',
          title: 'Integration Name',
          description: 'A friendly name for this HubSpot integration',
          minLength: 1,
          maxLength: 100
        },
        useOAuth: {
          type: 'boolean',
          title: 'Use OAuth 2.0',
          description: 'Use OAuth 2.0 authentication instead of API key',
          default: true
        },
        apiKey: {
          type: 'string',
          title: 'API Key',
          description: 'HubSpot private app access token',
          condition: { useOAuth: false }
        },
        clientId: {
          type: 'string',
          title: 'Client ID',
          description: 'OAuth app client ID from HubSpot',
          condition: { useOAuth: true }
        },
        clientSecret: {
          type: 'string',
          title: 'Client Secret',
          description: 'OAuth app client secret from HubSpot',
          format: 'password',
          condition: { useOAuth: true }
        },
        redirectUri: {
          type: 'string',
          title: 'Redirect URI',
          description: 'OAuth redirect URI configured in HubSpot app',
          format: 'uri',
          condition: { useOAuth: true }
        },
        portalId: {
          type: 'string',
          title: 'Portal ID',
          description: 'HubSpot portal (hub) ID (optional)'
        },
        scopes: {
          type: 'array',
          title: 'OAuth Scopes',
          description: 'Permissions requested from HubSpot',
          items: {
            type: 'string',
            enum: this.getValidScopes()
          },
          default: [
            'contacts',
            'timeline',
            'crm.objects.contacts.read',
            'crm.objects.contacts.write',
            'crm.objects.deals.read',
            'crm.objects.deals.write',
            'crm.objects.companies.read',
            'crm.objects.companies.write'
          ],
          condition: { useOAuth: true }
        },
        syncSettings: {
          type: 'object',
          title: 'Sync Settings',
          properties: {
            syncContacts: {
              type: 'boolean',
              title: 'Sync Contacts',
              default: true
            },
            syncDeals: {
              type: 'boolean',
              title: 'Sync Deals',
              default: false
            },
            syncCompanies: {
              type: 'boolean',
              title: 'Sync Companies',
              default: false
            },
            syncActivities: {
              type: 'boolean',
              title: 'Sync Email Activities',
              default: true
            },
            batchSize: {
              type: 'number',
              title: 'Batch Size',
              description: 'Number of records to process in each batch',
              minimum: 1,
              maximum: 100,
              default: 50
            }
          }
        }
      },
      required: ['name'],
      additionalProperties: false
    };
  }

  getSupportedFeatures(): string[] {
    return [
      'contacts_sync',
      'contacts_read',
      'contacts_write',
      'contacts_delete',
      'contacts_search',
      'activities_logging',
      'custom_properties',
      'deals_sync',
      'deals_read',
      'deals_write',
      'companies_sync',
      'companies_read',
      'companies_write',
      'api_key_auth',
      'oauth_authentication',
      'field_mapping',
      'conflict_resolution',
      'timeline_events',
      'webhook_support',
      'real_time_sync',
      'batch_operations',
      'incremental_sync',
      'bidirectional_sync'
    ];
  }

  private getValidScopes(): string[] {
    return [
      'contacts',
      'timeline',
      'crm.objects.contacts.read',
      'crm.objects.contacts.write',
      'crm.objects.deals.read',
      'crm.objects.deals.write',
      'crm.objects.companies.read',
      'crm.objects.companies.write',
      'crm.objects.line_items.read',
      'crm.objects.line_items.write',
      'crm.objects.quotes.read',
      'crm.objects.quotes.write',
      'crm.objects.tickets.read',
      'crm.objects.tickets.write',
      'crm.schemas.contacts.read',
      'crm.schemas.contacts.write',
      'crm.schemas.deals.read',
      'crm.schemas.deals.write',
      'crm.schemas.companies.read',
      'crm.schemas.companies.write',
      'integration-sync',
      'oauth',
      'forms',
      'files',
      'hubdb',
      'reports',
      'social',
      'automation',
      'business-intelligence'
    ];
  }
}

// Export factory instance
export const hubspotIntegrationFactory = new HubSpotIntegrationFactory();